import React, { useState, useEffect } from 'react'
import { useUser } from 'util/UserContext'
import { Navigate } from 'react-router-dom'
import { ChevronUp, ChevronDown } from 'lucide-react'
import api from 'config/axiosConfig'
import { Feedback } from 'util/constants'
import NotFoundPage from 'components/ResponsePages/404NotFoundPage'
import { AxiosError } from 'axios'
import ServerErrorPage from 'components/ResponsePages/500ServerErrorPage'
import { Button } from 'ui/Button'
import { formatDateWithoutTimezone } from 'config/dayjsConfig'
import { apiRoutes } from "util/apiRoutes";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from 'ui/Select'

interface AdminFeedbackProps {
    isAdmin: boolean
}

interface RowProps {
    feedback: Feedback
    handleStatusChange: (feedbackId: Feedback['id'], newStatus: Feedback['status']) => void
    handleSave: (feedbackId: Feedback['id']) => void
}

const FEEDBACK_STATUSES = [
    'Open',
    'Reviewing',
    'Planned',
    'Unplanned',
    'Developing',
    'Preview',
    'Released',
    'Closed'
]

const formatTimestamp = (timestamp: string): string => {
    const parsedDate = new Date(timestamp)
    return Number.isNaN(parsedDate.getTime())
        ? timestamp
        : formatDateWithoutTimezone(parsedDate)
}

const Row = ({
    feedback,
    handleStatusChange,
    handleSave,
}: RowProps) => {
    const [open, setOpen] = useState(false)

    return (
        <React.Fragment>
            <tr className="admin-feedback-row border-b border-eoe-border-main dark:border-eoe-dark-border-main">
                <td className="admin-feedback-cell px-3 py-2">{feedback.id}</td>
                <td className="admin-feedback-cell px-3 py-2">{feedback.username}</td>
                <td className="admin-feedback-cell px-3 py-2">{feedback.properties?.category || "—"}</td>
                <td className="admin-feedback-cell px-3 py-2">
                    <a
                        href={`/feedback/${feedback.id}`}
                        className="admin-feedback-title-link text-eoe-link-blue dark:text-eoe-dark-link-blue hover:underline"
                    >
                        {feedback.title}
                    </a>
                </td>
                <td className="admin-feedback-cell px-3 py-2">{feedback.vote_count}</td>
                <td className="admin-feedback-cell px-3 py-2">
                    <Select
                        value={feedback.status as string}
                        onValueChange={(value) =>
                            handleStatusChange(feedback.id, value as Feedback['status'])
                        }
                    >
                        <SelectTrigger className="admin-feedback-status-select h-8 min-w-[11rem] rounded-md border border-eoe-border-main dark:border-eoe-dark-border-main">
                            <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent className="admin-feedback-status-content">
                            {FEEDBACK_STATUSES.map((status) => (
                                <SelectItem key={status} value={status}>
                                    {status}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </td>
                <td className="admin-feedback-cell px-3 py-2">
                    {formatTimestamp(feedback.created_at)}
                </td>
                <td className="admin-feedback-cell px-3 py-2">
                    {formatTimestamp(feedback.updated_at)}
                </td>
                <td className="admin-feedback-cell px-3 py-2">
                    <button
                        type="button"
                        aria-label="expand row"
                        className="admin-feedback-expand-button inline-flex size-8 items-center justify-center rounded-full text-eoe-text-sub hover:bg-eoe-icon-hover dark:text-eoe-dark-text-sub dark:hover:bg-eoe-dark-icon-hover transition-colors"
                        onClick={() => setOpen(!open)}
                    >
                        {open ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
                    </button>
                </td>
                <td className="admin-feedback-cell px-3 py-2">
                    <Button
                        size="sm"
                        className="admin-feedback-save-button h-8 px-3 text-xs"
                        onClick={() => handleSave(feedback.id)}
                    >
                        Save
                    </Button>
                </td>
            </tr>
            <tr className={`admin-feedback-details-row ${open ? '' : 'hidden'}`}>
                <td className="admin-feedback-details-cell px-3 py-2" colSpan={10}>
                    <div className="admin-feedback-details-panel rounded-md border border-eoe-border-main dark:border-eoe-dark-border-main bg-eoe-bg dark:bg-eoe-dark-bg-modal p-3 space-y-2">
                        <h3 className="admin-feedback-details-heading text-base font-semibold text-eoe-text dark:text-eoe-dark-text">
                            Details
                        </h3>
                        <div className="admin-feedback-description-group grid grid-cols-1 gap-1">
                            <span className="admin-feedback-description-label text-xs font-semibold uppercase text-eoe-text-meta dark:text-eoe-dark-text-meta">
                                Body
                            </span>
                            <p className="admin-feedback-description-value text-sm text-eoe-text dark:text-eoe-dark-text">
                                {feedback.body || 'No body provided.'}
                            </p>
                        </div>
                    </div>
                </td>
            </tr>
        </React.Fragment>
    )
}

const AdminFeedback = ({ isAdmin }: AdminFeedbackProps) => {
    const { currentUser, awaitingUser } = useUser()
    const [feedbacks, setFeedbacks] = useState<Feedback[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (!awaitingUser && isAdmin) {
            fetchFeedbacks()
        }
    }, [awaitingUser, isAdmin])

    const fetchFeedbacks = async () => {
        try {
            const response = await api.get(apiRoutes.posts.root, { params: { kind: "feedback" } })
            setFeedbacks(response.data)
            setLoading(false)
        } catch (error) {
            if (error instanceof AxiosError && error.response?.status === 404) {
                setError('404: Failed to fetch feedback')
            } else {
                console.error('Error fetching feedback:', error)
                setError('Failed to fetch feedback')
            }
            setLoading(false)
        }
    }

    const handleStatusChange = (feedbackId: number, newStatus: string) => {
        setFeedbacks((currentFeedbacks) =>
            currentFeedbacks.map((feedback) =>
                feedback.id === feedbackId
                    ? { ...feedback, status: newStatus }
                    : feedback
            )
        )
    }

    const handleSave = async (feedbackId: number) => {
        const feedback = feedbacks.find((f) => f.id === feedbackId)
        if (!feedback) return

        try {
            await api.patch(apiRoutes.posts.byId(feedbackId), {
                status: feedback.status,
                category: feedback.properties.category,
                body: feedback.body,

            })
            // Re-fetch to ensure saved server state is reflected
            await fetchFeedbacks()
        } catch (error) {
            console.error('Error updating feedback:', error)
            setError('Failed to update feedback')
        }
    }

    if (awaitingUser) {
        return (
            <div className="admin-feedback-awaiting-user p-4 text-sm text-eoe-text dark:text-eoe-dark-text">
                Loading...
            </div>
        )
    }

    if (!currentUser || !currentUser.is_admin) {
        return <Navigate to="/" />
    }

    if (loading) {
        return (
            <div className="admin-feedback-loading p-4 text-sm text-eoe-text dark:text-eoe-dark-text">
                Loading...
            </div>
        )
    }

    if (error) return error.match(/404/) ? <NotFoundPage /> : <ServerErrorPage />

    return (
        <div className="admin-feedback-table-container overflow-x-auto rounded-md border border-eoe-border-main dark:border-eoe-dark-border-main bg-eoe-bg dark:bg-eoe-dark-bg">
            <table className="admin-feedback-table min-w-[1024px] w-full border-collapse text-left text-sm text-eoe-text dark:text-eoe-dark-text">
                <thead className="admin-feedback-table-head bg-eoe-icon dark:bg-eoe-dark-icon">
                    <tr className="admin-feedback-header-row border-b border-eoe-border-main dark:border-eoe-dark-border-main">
                        <th className="admin-feedback-header-cell px-3 py-2 font-semibold">ID</th>
                        <th className="admin-feedback-header-cell px-3 py-2 font-semibold">Username</th>
                        <th className="admin-feedback-header-cell px-3 py-2 font-semibold">Category</th>
                        <th className="admin-feedback-header-cell px-3 py-2 font-semibold">Title</th>
                        <th className="admin-feedback-header-cell px-3 py-2 font-semibold">Votes</th>
                        <th className="admin-feedback-header-cell px-3 py-2 font-semibold">Status</th>
                        <th className="admin-feedback-header-cell px-3 py-2 font-semibold">Created</th>
                        <th className="admin-feedback-header-cell px-3 py-2 font-semibold">Updated</th>
                        <th className="admin-feedback-header-cell px-3 py-2 font-semibold">Details</th>
                        <th className="admin-feedback-header-cell px-3 py-2 font-semibold">Actions</th>
                    </tr>
                </thead>
                <tbody className="admin-feedback-table-body">
                    {feedbacks.map((feedback) => (
                        <Row
                            key={feedback.id}
                            feedback={feedback}
                            handleStatusChange={handleStatusChange}
                            handleSave={handleSave}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    )
}

export default AdminFeedback
